import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Shield, AlertTriangle, HelpCircle } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Heart className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Jeu Responsable
            </h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              Chez Lyon Picks, nous croyons fermement que les paris en ligne doivent rester un divertissement sûr et contrôlé. Le jeu responsable 
              est au cœur de nos valeurs, et nous nous engageons à fournir des informations et des ressources pour aider nos utilisateurs à 
              parier de manière responsable.
            </p>

            <div className="bg-red-50 border-l-4 border-red-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                Avertissement Important
              </h2>
              <p className="font-semibold">
                Les paris en ligne peuvent être addictifs et entraîner des pertes financières importantes. Ne pariez jamais plus que vous ne 
                pouvez vous permettre de perdre. Les paris doivent rester un divertissement, pas un moyen de gagner de l'argent ou de résoudre 
                des problèmes financiers.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-purple-600" />
                Qu'est-ce que le Jeu Responsable ?
              </h2>
              <p>
                Le jeu responsable consiste à parier de manière contrôlée et consciente, en fixant des limites claires et en respectant ces limites. 
                Cela signifie :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Ne jamais parier plus que vous ne pouvez vous permettre de perdre</li>
                <li>Fixer des limites de temps et d'argent avant de commencer</li>
                <li>Ne jamais essayer de "rattraper" vos pertes en augmentant vos mises</li>
                <li>Ne jamais parier sous l'influence de l'alcool ou de drogues</li>
                <li>Ne jamais parier quand vous êtes émotionnellement perturbé</li>
                <li>Considérer les paris comme un divertissement, pas comme une source de revenus</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Signes d'Alerte</h2>
              <p>
                Il est important de reconnaître les signes d'un comportement de jeu problématique. Si vous reconnaissez ces signes chez vous ou 
                chez quelqu'un que vous connaissez, il est temps de demander de l'aide :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Parier plus que vous ne pouvez vous permettre de perdre</li>
                <li>Essayer de "rattraper" vos pertes en augmentant vos mises</li>
                <li>Mentir sur vos habitudes de jeu à votre famille ou vos amis</li>
                <li>Négliger vos responsabilités (travail, famille, études) à cause des paris</li>
                <li>Ressentir de l'anxiété, de la culpabilité ou de la dépression liées aux paris</li>
                <li>Emprunter de l'argent ou vendre des biens pour financer vos paris</li>
                <li>Penser constamment aux paris ou à vos prochains paris</li>
                <li>Ne pas pouvoir arrêter de parier même quand vous le souhaitez</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Outils de Jeu Responsable</h2>
              <p>
                Tous les sites de paris agréés en France doivent offrir des outils de jeu responsable. Ces outils vous permettent de :
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Limites de Dépôt</h3>
              <p>
                Fixez un montant maximum que vous pouvez déposer par jour, semaine ou mois. Ces limites sont activables immédiatement et doivent 
                être respectées par le site.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Limites de Mise</h3>
              <p>
                Limitez le montant que vous pouvez miser sur une période donnée. Cela vous aide à contrôler vos dépenses et à éviter les mises 
                excessives.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Limites de Temps</h3>
              <p>
                Limitez votre temps de jeu quotidien ou hebdomadaire. Le site vous rappellera quand vous approchez de votre limite.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Auto-Exclusion Temporaire</h3>
              <p>
                Suspendez votre compte pour une période déterminée (24 heures, 7 jours, 30 jours, etc.). Pendant cette période, vous ne pourrez 
                pas accéder à votre compte.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Auto-Exclusion Définitive</h3>
              <p>
                Fermez votre compte de manière permanente. Cette décision est irréversible pendant la période choisie et offre une protection 
                totale contre les rechutes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <HelpCircle className="w-6 h-6 text-purple-600" />
                Ressources d'Aide
              </h2>
              <p>
                Si vous pensez avoir un problème de jeu, de nombreuses ressources sont disponibles pour vous aider :
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">SOS Joueurs</h3>
                <p className="mb-2">
                  Organisation d'aide gratuite et confidentielle pour les joueurs en difficulté.
                </p>
                <p>
                  <strong>Site web :</strong> <a href="https://www.sosjoueurs.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">sosjoueurs.org</a>
                </p>
                <p>
                  <strong>Téléphone :</strong> 09 74 75 13 13 (appel gratuit)
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-600 p-6 my-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Joueurs Info Service</h3>
                <p className="mb-2">
                  Service d'information et d'aide pour le jeu responsable.
                </p>
                <p>
                  <strong>Site web :</strong> <a href="https://www.joueurs-info-service.fr/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 underline">joueurs-info-service.fr</a>
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-600 p-6 my-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Evalujeu</h3>
                <p className="mb-2">
                  Test d'auto-évaluation pour identifier les risques liés au jeu.
                </p>
                <p>
                  <strong>Site web :</strong> <a href="https://www.evalujeu.fr/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">evalujeu.fr</a>
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 my-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Autorité Nationale des Jeux (ANJ)</h3>
                <p className="mb-2">
                  Organisme de régulation qui fournit des informations sur le jeu responsable.
                </p>
                <p>
                  <strong>Site web :</strong> <a href="https://www.anj.fr/" target="_blank" rel="noopener noreferrer" className="text-yellow-600 hover:text-yellow-700 underline">anj.fr</a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Conseils pour Jouer de Manière Responsable</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Fixez un budget</strong> : Déterminez combien vous pouvez vous permettre de perdre avant de commencer à parier</li>
                <li><strong>Respectez votre budget</strong> : Ne dépassez jamais le montant que vous avez fixé</li>
                <li><strong>Ne jouez jamais sous l'influence</strong> : L'alcool et les drogues altèrent votre jugement</li>
                <li><strong>Prenez des pauses régulières</strong> : Ne laissez pas les paris devenir une activité constante</li>
                <li><strong>Ne cherchez pas à rattraper vos pertes</strong> : C'est le chemin le plus rapide vers des problèmes</li>
                <li><strong>Considérez les pertes comme le coût du divertissement</strong> : Ne vous attendez pas à gagner</li>
                <li><strong>Ne pariez jamais sur des émotions</strong> : Restez objectif et rationnel</li>
                <li><strong>Utilisez les outils de jeu responsable</strong> : Ils sont là pour vous aider</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Notre Engagement</h2>
              <p>
                Chez Lyon Picks, nous nous engageons à promouvoir le jeu responsable. Nous fournissons des informations sur les outils de jeu 
                responsable disponibles, nous encourageons l'utilisation de ces outils, et nous fournissons des liens vers les ressources d'aide.
              </p>
              <p>
                Nous ne recommandons jamais de parier plus que vous ne pouvez vous permettre de perdre, et nous encourageons toujours nos 
                utilisateurs à utiliser les outils de jeu responsable disponibles sur les sites de paris.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
              <p>
                Si vous avez des questions sur le jeu responsable ou si vous avez besoin d'aide, n'hésitez pas à nous contacter ou à contacter 
                une des organisations d'aide mentionnées ci-dessus :
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mt-4">
                <p><strong>Email :</strong> <a href="mailto:info@lyonpicks.com" className="text-purple-600 hover:text-purple-700 underline">info@lyonpicks.com</a></p>
                <p><strong>Téléphone :</strong> <a href="tel:+33289858716" className="text-purple-600 hover:text-purple-700 underline">+33 2 89 85 87 16</a></p>
                <p><strong>Adresse :</strong> Paris, France</p>
              </div>
            </section>

            <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-8 text-white my-8">
              <h2 className="text-2xl font-bold mb-4">Rappelez-vous</h2>
              <p className="text-lg leading-relaxed">
                Les paris en ligne doivent rester un divertissement. Si vous ne pouvez plus contrôler vos paris, ou si les paris causent des 
                problèmes dans votre vie, il est temps de demander de l'aide. Le jeu responsable n'est pas une faiblesse : c'est une approche 
                mature et saine qui vous permet de profiter des paris en ligne de manière durable et contrôlée.
              </p>
            </div>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

